// SeminarCountdownChip — persistent pill that displays "Middleton Seminar · Nd".
//
// Two visual variants:
//   • "light"  → default, sits in the white V3 TopBar (amber-tinted)
//   • "dark"   → for dark navy headers (Investor Fuel landing)
//   • "warm"   → for cream-background landings (Middleton, Velocity Play)
//
// Auto-hides once the seminar date has passed.

import React from "react";
import { Link } from "react-router-dom";
import { CalendarClock } from "lucide-react";
import {
  seminarCountdown,
  SEMINAR_LABEL,
  SEMINAR_HREF,
  SEMINAR_PUBLIC_HREF,
} from "@/config/seminar";

const VARIANTS = {
  light: {
    // Sits on white — amber tint on hover, light background
    cls: "border-amber-300 bg-amber-50/70 hover:bg-amber-100 text-amber-900",
    icon: "text-amber-600",
    days: "text-amber-800",
  },
  warm: {
    // Sits on cream (#FFFBEB) — subtle amber ring, stronger contrast
    cls: "border-amber-400 bg-white hover:bg-amber-50 text-amber-900 shadow-sm",
    icon: "text-amber-600",
    days: "text-amber-700",
  },
  dark: {
    // Sits on navy — amber-on-navy, soft glow
    cls: "border-amber-400/60 bg-amber-500/10 hover:bg-amber-500/20 text-amber-200",
    icon: "text-amber-300",
    days: "text-amber-100",
  },
};

export default function SeminarCountdownChip({
  variant = "light",
  publicPage = false,
  hideBelow = "sm",        // "sm" = hide on <640px, "md" = hide on <768px, "never" = always show
  testid = "seminar-countdown-chip",
}) {
  const { days, expired } = seminarCountdown();
  if (expired) return null;

  const v = VARIANTS[variant] || VARIANTS.light;
  const href = publicPage ? SEMINAR_PUBLIC_HREF : SEMINAR_HREF;

  const hideCls = hideBelow === "never"
    ? ""
    : hideBelow === "md"
      ? "hidden md:inline-flex"
      : "hidden sm:inline-flex";

  const daysLabel = days === 0 ? "TODAY" : days === 1 ? "1d" : `${days}d`;

  return (
    <Link
      to={href}
      data-testid={testid}
      title={`${SEMINAR_LABEL} — ${days} day${days === 1 ? "" : "s"} away`}
      className={[
        hideCls,
        "items-center gap-1.5 rounded-full border pl-2 pr-2.5 py-1 text-[11.5px] font-semibold transition-all",
        v.cls,
      ].join(" ")}
    >
      <CalendarClock className={`w-3.5 h-3.5 ${v.icon}`} />
      <span className="truncate max-w-[140px]">{SEMINAR_LABEL}</span>
      <span className="text-slate-400/60 font-normal">·</span>
      <span className={`tabular-nums font-black ${v.days}`}>{daysLabel}</span>
    </Link>
  );
}
